// ── Pattern Canvas Builder ────────────────────────────────────
// Interactive drag-and-drop visual editor for thinking pattern DAGs.
// Uses React Flow for the canvas, with a stage palette and inspector panel.

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// ── Constants ────────────────────────────────────────────────

const STAGE_TYPES = [
  { type: 'generate',  icon: '⚡', label: 'AI Generate',  color: '#6c63ff', desc: 'Stream AI-generated nodes' },
  { type: 'transform', icon: '⚙',  label: 'AI Transform', color: '#f59e0b', desc: 'Single AI call, returns data' },
  { type: 'branch',    icon: '⑂',  label: 'Branch',       color: '#ff4757', desc: 'Conditional routing' },
  { type: 'fan_out',   icon: '⤡',  label: 'Fan Out',      color: '#ec4899', desc: 'Parallel execution' },
  { type: 'merge',     icon: '⊕',  label: 'Merge',        color: '#0ea5e9', desc: 'Combine parallel results' },
  { type: 'loop',      icon: '↻',  label: 'Loop',         color: '#cc5de8', desc: 'Repeat until condition' },
  { type: 'score',     icon: '📊', label: 'Score',        color: '#22c55e', desc: 'Evaluate against dimensions' },
  { type: 'filter',    icon: '⧩',  label: 'Filter',       color: '#f97316', desc: 'Prune by threshold' },
  { type: 'enrich',    icon: '🔍', label: 'Enrich',       color: '#14b8a6', desc: 'Add research/context' },
  { type: 'refine',    icon: '🔄', label: 'Refine',       color: '#a855f7', desc: 'Iterative critique-strengthen' },
  { type: 'fractal',   icon: '🌀', label: 'Fractal',      color: '#06b6d4', desc: 'Recursive node expansion' },
];

const MODELS = [
  { id: 'claude:opus',   label: 'Claude Opus' },
  { id: 'claude:sonnet', label: 'Claude Sonnet' },
  { id: 'gemini:pro',    label: 'Gemini Pro' },
  { id: 'gemini:flash',  label: 'Gemini Flash' },
];

function getStageType(type) {
  return STAGE_TYPES.find(s => s.type === type) || STAGE_TYPES[0];
}

// ── Custom Stage Node ────────────────────────────────────────

function StageNode({ data, selected }) {
  const st = getStageType(data.stageType);
  const isEntrypoint = data.isEntrypoint;
  const isTerminal = data.isTerminal;

  return (
    <div
      className={`pcb-node ${selected ? 'pcb-node--selected' : ''} ${isEntrypoint ? 'pcb-node--entry' : ''} ${isTerminal ? 'pcb-node--terminal' : ''}`}
      style={{ borderColor: selected ? st.color : undefined }}
    >
      <Handle type="target" position={Position.Top} className="pcb-handle" />

      {isEntrypoint && <div className="pcb-node-marker pcb-node-marker--start">START</div>}

      <div className="pcb-node-body">
        <span className="pcb-node-icon" style={{ color: st.color }}>{st.icon}</span>
        <div className="pcb-node-info">
          <span className="pcb-node-name">{data.stageName}</span>
          <span className="pcb-node-type" style={{ color: st.color }}>{st.label}</span>
        </div>
        {data.model && (
          <span className="pcb-node-model">{data.model.split(':')[1]}</span>
        )}
      </div>

      {isTerminal && <div className="pcb-node-marker pcb-node-marker--end">END</div>}

      <Handle type="source" position={Position.Bottom} className="pcb-handle" />
    </div>
  );
}

const nodeTypes = { stageNode: StageNode };

// ── Pattern → React Flow conversion ─────────────────────────

function patternToFlow(editDef) {
  if (!editDef?.stages || !editDef?.graph) return { nodes: [], edges: [] };

  const stages = editDef.stages;
  const graph = editDef.graph;
  const stageNames = Object.keys(stages);
  const graphEdges = graph.edges || [];

  // BFS layout
  const levels = {};
  const visited = new Set();
  const queue = [{ name: graph.entrypoint, level: 0 }];

  while (queue.length > 0) {
    const { name, level } = queue.shift();
    if (visited.has(name) || !stages[name]) continue;
    visited.add(name);
    levels[name] = Math.max(levels[name] || 0, level);

    const outgoing = graphEdges.filter(e => e.from === name);
    for (const edge of outgoing) {
      if (!visited.has(edge.to)) queue.push({ name: edge.to, level: level + 1 });
    }

    const stageDef = stages[name];
    if (stageDef?.type === 'branch') {
      if (stageDef.onTrue && !visited.has(stageDef.onTrue)) queue.push({ name: stageDef.onTrue, level: level + 1 });
      if (stageDef.onFalse && !visited.has(stageDef.onFalse)) queue.push({ name: stageDef.onFalse, level: level + 1 });
    }
    if (stageDef?.type === 'fan_out') {
      (stageDef.branches || []).forEach(b => {
        if (!visited.has(b)) queue.push({ name: b, level: level + 1 });
      });
    }
  }

  // Unvisited stages at bottom
  for (const name of stageNames) {
    if (levels[name] === undefined) levels[name] = Object.keys(levels).length;
  }

  // Group by level for x positioning
  const byLevel = {};
  for (const [name, level] of Object.entries(levels)) {
    if (!byLevel[level]) byLevel[level] = [];
    byLevel[level].push(name);
  }

  const NODE_GAP_X = 220;
  const NODE_GAP_Y = 120;

  const nodes = [];
  for (const [level, names] of Object.entries(byLevel)) {
    const totalWidth = (names.length - 1) * NODE_GAP_X;
    names.forEach((name, i) => {
      const stageDef = stages[name];
      nodes.push({
        id: name,
        type: 'stageNode',
        position: {
          x: i * NODE_GAP_X - totalWidth / 2 + 300,
          y: Number(level) * NODE_GAP_Y + 60,
        },
        data: {
          stageName: name,
          stageType: stageDef.type,
          model: stageDef.model,
          isEntrypoint: name === graph.entrypoint,
          isTerminal: !!stageDef.terminal,
        },
      });
    });
  }

  // Build edges
  const edges = [];
  const addedEdges = new Set();

  for (const edge of graphEdges) {
    const key = `${edge.from}-${edge.to}`;
    if (addedEdges.has(key)) continue;
    addedEdges.add(key);

    const isBack = (levels[edge.to] || 0) <= (levels[edge.from] || 0);
    edges.push({
      id: key,
      source: edge.from,
      target: edge.to,
      label: edge.condition || '',
      type: isBack ? 'default' : 'default',
      animated: isBack,
      style: { stroke: isBack ? '#ff4757' : '#555', strokeDasharray: isBack ? '5,5' : undefined },
      markerEnd: { type: MarkerType.ArrowClosed, color: isBack ? '#ff4757' : '#555' },
    });
  }

  // Branch implicit edges
  for (const [name, stageDef] of Object.entries(stages)) {
    if (stageDef.type === 'branch') {
      for (const [target, label] of [[stageDef.onTrue, '✓'], [stageDef.onFalse, '✗']]) {
        if (!target) continue;
        const key = `${name}-${target}`;
        if (addedEdges.has(key)) continue;
        addedEdges.add(key);
        const isBack = (levels[target] || 0) <= (levels[name] || 0);
        edges.push({
          id: key,
          source: name,
          target,
          label,
          animated: isBack,
          style: { stroke: isBack ? '#ff4757' : '#555', strokeDasharray: isBack ? '5,5' : undefined },
          markerEnd: { type: MarkerType.ArrowClosed, color: isBack ? '#ff4757' : '#555' },
        });
      }
    }

    // Fan-out implicit edges
    if (stageDef.type === 'fan_out') {
      for (const branch of (stageDef.branches || [])) {
        const key = `${name}-${branch}`;
        if (addedEdges.has(key)) continue;
        addedEdges.add(key);
        edges.push({
          id: key,
          source: name,
          target: branch,
          style: { stroke: '#ec4899' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#ec4899' },
        });
      }
      // Merge edge
      if (stageDef.mergeTo) {
        for (const branch of (stageDef.branches || [])) {
          const key = `${branch}-${stageDef.mergeTo}`;
          if (addedEdges.has(key)) continue;
          addedEdges.add(key);
          edges.push({
            id: key,
            source: branch,
            target: stageDef.mergeTo,
            style: { stroke: '#0ea5e9' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#0ea5e9' },
          });
        }
      }
    }
  }

  return { nodes, edges };
}

// ── Inspector Panel ──────────────────────────────────────────

function StageInspector({ stageName, stageDef, allStageNames, onUpdate, onDelete, onSetEntrypoint, isEntrypoint, promptText }) {
  if (!stageDef) return null;
  const st = getStageType(stageDef.type);

  const update = (field, value) => {
    onUpdate(stageName, { ...stageDef, [field]: value });
  };

  return (
    <div className="pcb-inspector">
      <div className="pcb-inspector-header">
        <span className="pcb-inspector-icon" style={{ color: st.color }}>{st.icon}</span>
        <span className="pcb-inspector-name">{stageName}</span>
        <span className="pcb-inspector-type" style={{ background: st.color }}>{st.label}</span>
      </div>

      <div className="pcb-inspector-section">
        <label>Stage Type</label>
        <select value={stageDef.type} onChange={e => update('type', e.target.value)}>
          {STAGE_TYPES.map(s => <option key={s.type} value={s.type}>{s.label}</option>)}
        </select>
      </div>

      {stageDef.type !== 'branch' && stageDef.type !== 'fan_out' && (
        <div className="pcb-inspector-section">
          <label>Model</label>
          <select value={stageDef.model || ''} onChange={e => update('model', e.target.value || undefined)}>
            <option value="">Auto</option>
            {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
      )}

      {/* Branch-specific */}
      {stageDef.type === 'branch' && (
        <>
          <div className="pcb-inspector-section">
            <label>Condition</label>
            <input
              value={stageDef.condition || ''}
              onChange={e => update('condition', e.target.value)}
              placeholder="{{critique.satisfied}} === true"
              className="pcb-inspector-input"
            />
          </div>
          <div className="pcb-inspector-section">
            <label>If True →</label>
            <select value={stageDef.onTrue || ''} onChange={e => update('onTrue', e.target.value)}>
              <option value="">None</option>
              {allStageNames.filter(n => n !== stageName).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="pcb-inspector-section">
            <label>If False →</label>
            <select value={stageDef.onFalse || ''} onChange={e => update('onFalse', e.target.value)}>
              <option value="">None</option>
              {allStageNames.filter(n => n !== stageName).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Fan-out specific */}
      {stageDef.type === 'fan_out' && (
        <>
          <div className="pcb-inspector-section">
            <label>Branches</label>
            <input
              value={(stageDef.branches || []).join(', ')}
              onChange={e => update('branches', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="expert_a, expert_b, expert_c"
              className="pcb-inspector-input"
            />
          </div>
          <div className="pcb-inspector-section">
            <label>Merge Into</label>
            <select value={stageDef.mergeTo || ''} onChange={e => update('mergeTo', e.target.value)}>
              <option value="">None</option>
              {allStageNames.filter(n => n !== stageName).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Prompt (for AI stages) */}
      {['generate', 'transform', 'score', 'enrich', 'merge'].includes(stageDef.type) && (
        <div className="pcb-inspector-section">
          <label>
            Prompt
            {stageDef.promptKey && (
              <span className="pcb-inspector-prompt-key" title="Linked to Prompts tab">
                🔗 {stageDef.promptKey}
              </span>
            )}
          </label>
          <textarea
            className="pcb-inspector-textarea"
            value={stageDef.promptFallback || promptText || ''}
            onChange={e => update('promptFallback', e.target.value)}
            placeholder="System prompt for this stage..."
            rows={6}
          />
        </div>
      )}

      {/* Toggles */}
      <div className="pcb-inspector-toggles">
        {stageDef.type !== 'branch' && (
          <label className="pcb-inspector-toggle">
            <input type="checkbox" checked={!!stageDef.stream} onChange={e => update('stream', e.target.checked)} />
            <span>Stream output</span>
          </label>
        )}
        <label className="pcb-inspector-toggle">
          <input type="checkbox" checked={!!stageDef.terminal} onChange={e => update('terminal', e.target.checked)} />
          <span>Terminal (end)</span>
        </label>
      </div>

      {/* Actions */}
      <div className="pcb-inspector-actions">
        {!isEntrypoint && (
          <button className="pcb-btn pcb-btn--ghost" onClick={() => onSetEntrypoint(stageName)}>
            Set as Entrypoint
          </button>
        )}
        <button className="pcb-btn pcb-btn--danger" onClick={() => onDelete(stageName)}>
          Delete Stage
        </button>
      </div>
    </div>
  );
}

// ── Stage Palette ────────────────────────────────────────────

function StagePalette({ onAdd }) {
  return (
    <div className="pcb-palette">
      <div className="pcb-palette-title">STAGES</div>
      {STAGE_TYPES.map(st => (
        <div
          key={st.type}
          className="pcb-palette-item"
          draggable
          onDragStart={e => {
            e.dataTransfer.setData('application/pcb-stage-type', st.type);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onClick={() => onAdd(st.type)}
          title={st.desc}
        >
          <span className="pcb-palette-icon" style={{ color: st.color }}>{st.icon}</span>
          <span className="pcb-palette-label">{st.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Canvas Builder ──────────────────────────────────────

function PatternCanvasBuilderInner({ editDef, setEditDef, testRunning, executingStage }) {
  const [selectedNode, setSelectedNode] = useState(null);
  const reactFlowRef = useRef(null);

  // Convert pattern def to React Flow nodes/edges
  const initial = useMemo(() => patternToFlow(editDef), [editDef]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);

  // Sync when editDef changes externally (e.g. selecting a different pattern)
  const prevDefRef = useRef(null);
  useEffect(() => {
    const defStr = JSON.stringify(editDef?.stages) + JSON.stringify(editDef?.graph);
    if (defStr !== prevDefRef.current) {
      prevDefRef.current = defStr;
      const flow = patternToFlow(editDef);
      setNodes(flow.nodes);
      setEdges(flow.edges);
      setSelectedNode(null);
    }
  }, [editDef, setNodes, setEdges]);

  // Handle new edge connections
  const onConnect = useCallback((params) => {
    // Add to React Flow
    setEdges(eds => addEdge({
      ...params,
      markerEnd: { type: MarkerType.ArrowClosed, color: '#555' },
      style: { stroke: '#555' },
    }, eds));

    // Add to pattern def
    setEditDef(prev => ({
      ...prev,
      graph: {
        ...prev.graph,
        edges: [...(prev.graph?.edges || []), { from: params.source, to: params.target }],
      },
    }));
  }, [setEdges, setEditDef]);

  // Handle node selection
  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Handle node position changes (drag)
  const onNodeDragStop = useCallback((_, node) => {
    // Position is stored in React Flow state, not in pattern def
  }, []);

  // Drop handler for palette items
  const onDrop = useCallback((event) => {
    event.preventDefault();
    const stageType = event.dataTransfer.getData('application/pcb-stage-type');
    if (!stageType) return;

    const bounds = reactFlowRef.current?.getBoundingClientRect();
    if (!bounds) return;

    const name = prompt('Stage name:');
    if (!name || !name.trim()) return;
    const stageName = name.trim().replace(/\s+/g, '_').toLowerCase();

    // Check for duplicate name
    if (editDef?.stages?.[stageName]) {
      alert(`Stage "${stageName}" already exists`);
      return;
    }

    const newStage = {
      type: stageType,
      model: stageType === 'branch' || stageType === 'fan_out' ? undefined : 'gemini:pro',
      promptFallback: '',
      stream: stageType === 'generate',
      terminal: false,
    };

    if (stageType === 'branch') {
      newStage.condition = '';
      newStage.onTrue = '';
      newStage.onFalse = '';
    }
    if (stageType === 'fan_out') {
      newStage.branches = [];
      newStage.mergeTo = '';
    }

    setEditDef(prev => ({
      ...prev,
      stages: { ...prev.stages, [stageName]: newStage },
    }));

    setSelectedNode(stageName);
  }, [editDef, setEditDef]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Add stage from palette click
  const handleAddFromPalette = useCallback((stageType) => {
    const name = prompt('Stage name:');
    if (!name || !name.trim()) return;
    const stageName = name.trim().replace(/\s+/g, '_').toLowerCase();

    if (editDef?.stages?.[stageName]) {
      alert(`Stage "${stageName}" already exists`);
      return;
    }

    const newStage = {
      type: stageType,
      model: stageType === 'branch' || stageType === 'fan_out' ? undefined : 'gemini:pro',
      promptFallback: '',
      stream: stageType === 'generate',
      terminal: false,
    };

    if (stageType === 'branch') {
      newStage.condition = '';
      newStage.onTrue = '';
      newStage.onFalse = '';
    }
    if (stageType === 'fan_out') {
      newStage.branches = [];
      newStage.mergeTo = '';
    }

    setEditDef(prev => ({
      ...prev,
      stages: { ...prev.stages, [stageName]: newStage },
    }));

    setSelectedNode(stageName);
  }, [editDef, setEditDef]);

  // Inspector callbacks
  const handleUpdateStage = useCallback((stageName, newDef) => {
    setEditDef(prev => ({
      ...prev,
      stages: { ...prev.stages, [stageName]: newDef },
    }));
  }, [setEditDef]);

  const handleDeleteStage = useCallback((stageName) => {
    if (!window.confirm(`Delete stage "${stageName}"?`)) return;

    setEditDef(prev => {
      const newStages = { ...prev.stages };
      delete newStages[stageName];

      // Clean edges
      const newEdges = (prev.graph?.edges || []).filter(
        e => e.from !== stageName && e.to !== stageName
      );

      // Fix entrypoint if deleted
      const newEntrypoint = prev.graph?.entrypoint === stageName
        ? Object.keys(newStages)[0] || ''
        : prev.graph?.entrypoint;

      return {
        ...prev,
        stages: newStages,
        graph: { ...prev.graph, entrypoint: newEntrypoint, edges: newEdges },
      };
    });

    setSelectedNode(null);
  }, [setEditDef]);

  const handleSetEntrypoint = useCallback((stageName) => {
    setEditDef(prev => ({
      ...prev,
      graph: { ...prev.graph, entrypoint: stageName },
    }));
  }, [setEditDef]);

  // Handle edge deletion
  const onEdgesDelete = useCallback((deletedEdges) => {
    setEditDef(prev => {
      const deletedKeys = new Set(deletedEdges.map(e => `${e.source}-${e.target}`));
      const newEdges = (prev.graph?.edges || []).filter(
        e => !deletedKeys.has(`${e.from}-${e.to}`)
      );
      return { ...prev, graph: { ...prev.graph, edges: newEdges } };
    });
  }, [setEditDef]);

  // Highlight executing stage during test
  const nodesWithExec = useMemo(() => {
    if (!executingStage) return nodes;
    return nodes.map(n => ({
      ...n,
      className: n.id === executingStage ? 'pcb-node-executing' : '',
    }));
  }, [nodes, executingStage]);

  const selectedStageDef = selectedNode ? editDef?.stages?.[selectedNode] : null;
  const allStageNames = Object.keys(editDef?.stages || {});

  return (
    <div className="pcb-layout">
      <StagePalette onAdd={handleAddFromPalette} />

      <div className="pcb-canvas-wrapper" ref={reactFlowRef}>
        <ReactFlow
          nodes={nodesWithExec}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onNodeDragStop={onNodeDragStop}
          onPaneClick={onPaneClick}
          onEdgesDelete={onEdgesDelete}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          deleteKeyCode="Delete"
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{
            type: 'default',
            markerEnd: { type: MarkerType.ArrowClosed, color: '#555' },
            style: { stroke: '#555' },
          }}
        >
          <Background color="#1a1a2e" gap={20} size={1} />
          <Controls showInteractive={false} />
          <MiniMap
            nodeColor={(n) => {
              const st = getStageType(n.data?.stageType);
              return st.color;
            }}
            style={{ background: '#0a0a14' }}
            maskColor="rgba(0,0,0,0.7)"
          />
        </ReactFlow>
      </div>

      {selectedStageDef && (
        <StageInspector
          stageName={selectedNode}
          stageDef={selectedStageDef}
          allStageNames={allStageNames}
          onUpdate={handleUpdateStage}
          onDelete={handleDeleteStage}
          onSetEntrypoint={handleSetEntrypoint}
          isEntrypoint={selectedNode === editDef?.graph?.entrypoint}
        />
      )}
    </div>
  );
}

export default function PatternCanvasBuilder(props) {
  return (
    <ReactFlowProvider>
      <PatternCanvasBuilderInner {...props} />
    </ReactFlowProvider>
  );
}
